import React, {useCallback} from 'react';
import Image from 'next/image';
import {MUIBox, MUITypography} from '@/components/MUI-Components';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {AI_TOOLS, buildAiPrompt, buildAiPromptUrl} from '@/constants/ai-tools';
import {colors, fontFamily, fontSize, fontWeight, borderRadius} from '@/styles/tokens';

// ─── AiToolsCard ─────────────────────────────────────────────────────────────
// "Analyze this article with AI" sidebar card.
//
// NOT blog-specific — usable on any long-form content page:
//   blogs, case studies, whitepapers, landing pages, docs, FAQs, CMS pages.
//
// All sx constants are module-level (not re-created on each render) and
// reference only design tokens from tokens.ts. No raw hex codes.
//
// The 768–1199px tablet band is NOT a standard MUI breakpoint (sm=600 / md=900
// / lg=1200), so it stays as a plain media-query key — the one place a raw
// query is the right tool.

const MOBILE_TABLET = '@media (min-width:768px) and (max-width:1199px)';

const sxCard = {
    border: `1px solid ${colors.accent.orangeTintBorder}`,
    borderRadius: borderRadius.lg,
    p: '1.125rem',
    background: colors.background.orangeTint,
    mb: '1.25rem',
    [MOBILE_TABLET]: {mb: 0, display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 0}
} as const;

const sxTag = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3125rem',
    background: colors.background.orangeTint,
    borderRadius: borderRadius.full,
    padding: '0.1875rem 0.625rem',
    mb: '0.75rem'
} as const;

const sxTagIcon = {fontSize: fontSize[14], color: colors.accent.orangeMid} as const;

const sxTagText = {
    fontFamily: fontFamily.body,
    fontSize: fontSize[12],
    fontWeight: fontWeight.semibold,
    color: colors.accent.orangeDeep,
    letterSpacing: '0.03em'
} as const;

const sxTitle = {
    fontFamily: fontFamily.heading,
    fontSize: fontSize[16],
    color: colors.text.primary,
    mb: '0.375rem',
    lineHeight: 1.3
} as const;

const sxSub = {
    fontFamily: fontFamily.body,
    fontSize: fontSize[14],
    color: colors.text.secondary,
    lineHeight: 1.55,
    mb: '0.625rem'
} as const;

const sxNoAccount = {
    display: 'inline-flex',
    alignItems: 'center',
    background: colors.background.default,
    border: `1.5px solid ${colors.accent.orangeDeep}`,
    borderRadius: borderRadius.full,
    padding: '0.1875rem 0.625rem',
    mb: '0.875rem',
    fontFamily: fontFamily.body,
    fontSize: fontSize[12],
    color: colors.accent.orangeDarkest,
    fontWeight: fontWeight.semibold,
    [MOBILE_TABLET]: {alignSelf: 'flex-start', width: 'fit-content'}
} as const;

const sxOptions = {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    m: '0 -1.125rem -1.125rem -1.125rem',
    borderRadius: `0 0 ${borderRadius.lg} ${borderRadius.lg}`,
    overflow: 'hidden',
    [MOBILE_TABLET]: {flex: '1 1 auto'}
} as const;

const sxOption = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0.625rem 0.75rem',
    borderRadius: 0,
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'filter 0.15s',
    mb: 0,
    textAlign: 'left',
    fontFamily: fontFamily.body,
    '&:hover': {filter: 'brightness(0.97)'},
    [MOBILE_TABLET]: {flex: '1 1 0', pt: '1rem', pb: '1rem'}
} as const;

const sxOptionLeft = {display: 'flex', alignItems: 'center', gap: '0.625rem'} as const;

const sxOptionInfo = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.125rem',
    textAlign: 'left'
} as const;

const sxOptionName = {
    fontFamily: fontFamily.body,
    fontSize: fontSize[14],
    fontWeight: fontWeight.semibold,
    color: colors.text.primary,
    [MOBILE_TABLET]: {fontSize: fontSize[16]}
} as const;

const sxOptionBy = {
    fontWeight: fontWeight.regular,
    color: colors.text.muted,
    fontSize: fontSize[12],
    [MOBILE_TABLET]: {fontSize: fontSize[14]}
} as const;

const sxOptionAction = {
    fontFamily: fontFamily.body,
    fontSize: fontSize[12],
    color: colors.text.muted,
    [MOBILE_TABLET]: {fontSize: fontSize[14]}
} as const;

const sxOptionArrow = {fontSize: fontSize[16], color: colors.text.muted, flexShrink: 0} as const;

interface AiToolsCardProps {
    // Article title + canonical URL — injected into the pre-filled AI prompt.
    // Optional so the card still renders on pages without article context.
    title?: string;
    url?: string;
}

const AiToolsCard = ({title = '', url = ''}: AiToolsCardProps) => {
    // "Copy for Agent" — copy the full prompt (incl. title + URL) to the clipboard.
    const handleCopy = useCallback(() => {
        if (typeof navigator === 'undefined' || !navigator.clipboard) return;
        navigator.clipboard.writeText(buildAiPrompt(title, url)).catch(() => {
            /* clipboard write failed — ignore */
        });
    }, [title, url]);

    return (
        <MUIBox sx={sxCard}>
            <MUIBox sx={sxTag}>
                <AutoAwesomeIcon sx={sxTagIcon} />
                <MUIBox component="span" sx={sxTagText}>
                    AI-Powered Reading
                </MUIBox>
            </MUIBox>
            <MUITypography sx={sxTitle}>Analyze this article with AI</MUITypography>
            <MUITypography sx={sxSub}>Send to your preferred AI for summaries, Q&amp;A, or deeper analysis.</MUITypography>
            <MUIBox sx={sxNoAccount}>
                <span>&#10003;</span>&nbsp;No account needed
            </MUIBox>
            <MUIBox sx={sxOptions}>
                {AI_TOOLS.map((ai) => {
                    const href = ai.href ? buildAiPromptUrl(ai.href, ai.promptParam as string, title, url) : null;
                    return (
                        <MUIBox
                            key={ai.name}
                            sx={{...sxOption, background: ai.bgColor}}
                            component={href ? 'a' : 'button'}
                            {...(href ? {href, target: '_blank', rel: 'noopener noreferrer'} : {type: 'button', onClick: handleCopy})}
                        >
                            <MUIBox sx={sxOptionLeft}>
                                <Image
                                    src={ai.iconSrc}
                                    alt={ai.name}
                                    width={28}
                                    height={28}
                                    style={{flexShrink: 0, borderRadius: '0.375rem'}}
                                />
                                <MUIBox sx={sxOptionInfo}>
                                    <MUIBox component="span" sx={sxOptionName}>
                                        {ai.name}&nbsp;
                                        <MUIBox component="span" sx={sxOptionBy}>
                                            {ai.showBy ? 'by ' : ''}
                                            {ai.by}
                                        </MUIBox>
                                    </MUIBox>
                                    <MUIBox component="span" sx={sxOptionAction}>
                                        {ai.action}
                                    </MUIBox>
                                </MUIBox>
                            </MUIBox>
                            <MUIBox component="span" sx={sxOptionArrow}>
                                &#8594;
                            </MUIBox>
                        </MUIBox>
                    );
                })}
            </MUIBox>
        </MUIBox>
    );
};

export default AiToolsCard;
